import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Folder, FileText, Upload, FolderUp, FolderPlus, Trash2, ChevronRight, Loader2, Lock } from "lucide-react";
import { api, getToken } from "../../../software/api";
import { isImageFile, ImageLightbox } from "../common/FileViewer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Walk dropped folders (webkitGetAsEntry) so a whole folder tree can be dragged in.
const readEntry = (entry, path = "") => new Promise((resolve) => {
  if (entry.isFile) { entry.file((f) => resolve([{ file: f, rel: path + f.name }]), () => resolve([])); return; }
  const reader = entry.createReader(); const all = [];
  const next = () => reader.readEntries(async (ents) => {
    if (!ents.length) { const nested = await Promise.all(all.map((e) => readEntry(e, `${path}${entry.name}/`))); resolve(nested.flat()); return; }
    all.push(...ents); next();
  }, () => resolve([]));
  next();
});

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function CompanyDocs() {
  const [folder, setFolder] = useState("");
  const [data, setData] = useState({ folders: [], files: [] });
  const [busy, setBusy] = useState("");
  const [drag, setDrag] = useState(false);
  const [srcs, setSrcs] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const fileIn = useRef(null), dirIn = useRef(null);
  const blob = (id) => fetch(`${BACKEND}/api/private-docs/file/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } }).then((r) => (r.ok ? r.blob() : null));
  // Private files aren't public URLs: fetch each picture with the login token for thumbnails + lightbox.
  useEffect(() => {
    let live = true;
    data.files.filter(isImageFile).filter((f) => !srcs[f.id]).forEach((f) => blob(f.id).then((b) => b && live && setSrcs((m) => ({ ...m, [f.id]: URL.createObjectURL(b) }))));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.files]);
  const images = useMemo(() => data.files.filter((f) => isImageFile(f) && srcs[f.id]).map((f) => ({ ...f, src: srcs[f.id] })), [data.files, srcs]);
  const openImg = (f) => setLightbox(Math.max(0, images.findIndex((x) => x.id === f.id)));
  const load = useCallback(() => api("GET", `/private-docs?folder=${encodeURIComponent(folder)}`).then(setData).catch((e) => toast.error(e.message)), [folder]);
  useEffect(() => { load(); }, [load]);

  const post = (url, body) => fetch(`${BACKEND}/api${url}`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body });
  // Big files (e.g. the 700MB Spa Buddy database) go up in 8MB pieces, each retried up to 3 times.
  const uploadBig = async (file, rel, label) => {
    const init = await api("POST", "/private-docs/upload/init", { name: file.name, relpath: rel, folder, size: file.size });
    const n = Math.ceil(file.size / init.chunk_size);
    for (let i = 0; i < n; i++) {
      const fd = new FormData(); fd.append("index", String(i)); fd.append("chunk", file.slice(i * init.chunk_size, (i + 1) * init.chunk_size), "part");
      let ok = false;
      for (let t = 0; t < 3 && !ok; t++) ok = (await post(`/private-docs/upload/${init.upload_id}/chunk`, fd).catch(() => ({ ok: false }))).ok;
      if (!ok) throw new Error("Connection dropped — please upload this file again");
      setBusy(`${label} ${file.name} — ${Math.round(((i + 1) / n) * 100)}%`);
    }
    await api("POST", `/private-docs/upload/${init.upload_id}/complete`);
  };
  const upload = async (items) => {
    if (!items.length) return;
    let ok = 0;
    for (const [i, { file, rel }] of items.entries()) {
      const label = `Uploading ${i + 1} of ${items.length}:`;
      setBusy(`${label} ${file.name}`);
      try {
        if (file.size > 1024 * 1024 * 1024) throw new Error("File too large (max 1GB)");
        if (file.size > 20 * 1024 * 1024) { await uploadBig(file, rel || file.name, label); ok++; continue; }
        const fd = new FormData(); fd.append("file", file); fd.append("folder", folder); fd.append("relpath", rel || file.name);
        const r = await post("/private-docs/upload", fd);
        if (r.ok) ok++; else throw new Error((await r.json().catch(() => ({}))).detail || "Upload failed");
      } catch (e) { toast.error(`${file.name}: ${e.message}`); }
    }
    setBusy(""); if (ok) toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`); load();
  };
  const fromInput = (list, useRel) => upload(Array.from(list || []).map((f) => ({ file: f, rel: useRel ? f.webkitRelativePath : f.name })));
  const onDrop = async (e) => {
    e.preventDefault(); setDrag(false);
    const entries = Array.from(e.dataTransfer.items || []).map((it) => it.webkitGetAsEntry && it.webkitGetAsEntry()).filter(Boolean);
    if (entries.length) upload((await Promise.all(entries.map((en) => readEntry(en)))).flat());
    else fromInput(e.dataTransfer.files, false);
  };
  const open = async (f) => {
    if (f.size > 20 * 1024 * 1024) setBusy(`Downloading ${f.name}…`);
    const r = await fetch(`${BACKEND}/api/private-docs/file/${f.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) { setBusy(""); toast.error("Could not open file"); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement("a"); a.href = url; a.target = "_blank";
    if (!/^(application\/pdf|image\/|text\/)/.test(f.content_type || "")) a.download = f.name;
    a.click(); setBusy(""); setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const newFolder = async () => {
    const n = window.prompt("New folder name"); if (!n) return;
    await api("POST", "/private-docs/folder", { path: folder ? `${folder}/${n}` : n }).catch((e) => toast.error(e.message)); load();
  };
  const delFile = async (f) => { if (!window.confirm(`Delete "${f.name}"?`)) return; await api("DELETE", `/private-docs/file/${f.id}`); load(); };
  const delFolder = async (p) => { if (!window.confirm(`Delete folder "${p}" and everything in it?`)) return; await api("DELETE", `/private-docs/folder?path=${encodeURIComponent(p)}`); load(); };
  const crumbs = folder ? folder.split("/") : [];
  const btn = "inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-bold text-slate-700 hover:bg-slate-100 transition-colors";

  return (
    <div data-testid="company-docs" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
      <div className="sticky -top-4 sm:-top-6 z-10 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 px-4 sm:px-6 pt-4 sm:pt-6 pb-1 bg-slate-50 border-b border-slate-200 mb-3" data-testid="cd-toolbar">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Lock className="h-4 w-4 text-amber-600" /><span className="text-[12px] text-slate-500">Private — Admin only. Agreements, insurances and other company papers.</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={newFolder} data-testid="cd-new-folder" className={btn}><FolderPlus className="h-4 w-4" />New Folder</button>
          <button onClick={() => fileIn.current.click()} data-testid="cd-upload-files" className={btn}><Upload className="h-4 w-4" />Upload Files</button>
          <button onClick={() => dirIn.current.click()} data-testid="cd-upload-folder" className={btn}><FolderUp className="h-4 w-4" />Upload Folder</button>
        </div>
        <input ref={fileIn} type="file" multiple hidden onChange={(e) => { fromInput(e.target.files, false); e.target.value = ""; }} data-testid="cd-file-input" />
        <input ref={dirIn} type="file" multiple hidden webkitdirectory="" directory="" onChange={(e) => { fromInput(e.target.files, true); e.target.value = ""; }} data-testid="cd-folder-input" />
      </div>
      <div className="flex items-center gap-1 text-[13px] font-bold mb-3" data-testid="cd-breadcrumb">
        <button onClick={() => setFolder("")} className="text-sky-700 hover:underline">Company Documents</button>
        {crumbs.map((c, i) => (
          <React.Fragment key={i}><ChevronRight className="h-3.5 w-3.5 text-slate-400" />
            <button onClick={() => setFolder(crumbs.slice(0, i + 1).join("/"))} className="text-sky-700 hover:underline">{c}</button></React.Fragment>
        ))}
      </div>
      </div>
      <div className={`rounded-lg border-2 border-dashed p-3 min-h-[260px] transition-colors ${drag ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white"}`} data-testid="cd-dropzone">
        {busy && <p className="flex items-center gap-2 text-[12px] font-bold text-sky-700 mb-2"><Loader2 className="h-4 w-4 animate-spin" />{busy}</p>}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-3">
          {data.folders.map((f) => (
            <div key={f.path} className="group relative rounded-md border border-slate-200 hover:border-sky-400 hover:bg-sky-50 transition-colors">
              <button onClick={() => setFolder(f.path)} data-testid={`cd-folder-${f.name}`} className="w-full flex flex-col items-center gap-1 p-3 text-[12px] font-bold text-slate-700">
                <Folder className="h-8 w-8 text-amber-500" fill="#fde68a" /><span className="truncate max-w-full">{f.name}</span><span className="text-[10px] text-slate-400">{f.count} file{f.count === 1 ? "" : "s"}</span>
              </button>
              <button onClick={() => delFolder(f.path)} data-testid={`cd-folder-delete-${f.name}`} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        {data.files.map((f) => (
          <div key={f.id} className="flex items-center gap-2 border-b border-slate-100 py-1.5 text-[13px]" data-testid={`cd-file-${f.id}`}>
            {isImageFile(f)
              ? <button onClick={() => openImg(f)} data-testid={`cd-thumb-${f.id}`} className="shrink-0 h-14 w-14 rounded overflow-hidden border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-sky-300">{srcs[f.id] && <img src={srcs[f.id]} alt={f.name} className="h-14 w-14 object-cover" />}</button>
              : <FileText className="h-4 w-4 text-slate-400 shrink-0" />}
            <button onClick={() => (isImageFile(f) ? openImg(f) : open(f))} className="flex-1 min-w-0 truncate text-left text-slate-800 hover:text-sky-700" title={f.name}>{f.name}</button>
            <span className="text-[11px] text-slate-400 shrink-0">{fmtSize(f.size || 0)}</span>
            <button onClick={() => delFile(f)} data-testid={`cd-file-delete-${f.id}`} className="text-rose-500 hover:text-rose-700 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {!data.folders.length && !data.files.length && !busy && (
          <p className="text-center text-slate-400 text-[13px] py-16" data-testid="cd-empty">Drag files or whole folders here, or use Upload Files / Upload Folder. Files up to 1GB each.</p>
        )}
      </div>
      {lightbox !== null && images.length > 0 && <ImageLightbox images={images} startIndex={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
